import * as React from 'react';
import { memo, useCallback, useEffect, useRef, type RefObject } from 'react';

import {
  ActionBarPrimitive,
  AuiIf,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  groupPartByType,
  MessagePartPrimitive,
  MessagePrimitive,
  useAui,
  useAuiState,
} from '@assistant-ui/react';
import { isToday as isTodayFn } from 'date-fns';
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  PencilIcon,
  RefreshCwIcon,
} from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';

import {
  GenericApprovalCard,
  type ApprovalPartState,
} from '@/components/assistant-ui/approval/generic-approval-card';
import { useMountedDuringStreaming } from '@/components/assistant-ui/grouped-tool-carousel';
import { useAwaitingHitlAction } from '@/components/assistant-ui/hitl/use-hitl-call';
import { MarkdownText } from '@/components/assistant-ui/markdown-text';
import { MessageLocaleWrapper } from '@/components/assistant-ui/message-locale-wrapper';
import { Reasoning, ReasoningGroup } from '@/components/assistant-ui/reasoning';
import { ToolDebugInfo } from '@/components/assistant-ui/tool-debug-info';
import { useToolGroup } from '@/components/assistant-ui/tool-group-provider';
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import { useConfig, useConfigValue } from '@/components/providers/config-provider';
import {
  isMessageDataPart,
  useMessageDataComponents,
} from '@/components/providers/data-renderers-provider';
import { useDebugMode } from '@/components/providers/debug-mode-provider';
import { useReadOnly } from '@/components/providers/read-only-provider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DynamicIcon } from '@/components/ui/dynamic-icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAnalytics } from '@/hooks/use-analytics';
import { useCanReloadMessage } from '@/hooks/use-can-reload-message';
import { useCharacterLimit } from '@/hooks/use-character-limit';
import { useMessageLocale } from '@/hooks/use-message-locale';
import { useSensitiveDataDetection } from '@/hooks/use-sensitive-data-detection';
import { useAssistantCloudStore } from '@/lib/assistant-cloud-local';
import { stopActiveTts } from '@/lib/tts/active-tts';
import { highlightWordInDom } from '@/lib/tts/highlight-word';
import { useTtsWordInfo } from '@/lib/tts/tts-signal';
import type { TenantAvatarConfig, TenantMessageAvatarsConfig, TtsConfig } from '@/lib/types/tenant';
import { cn } from '@/lib/utils';
import { queueDeletion } from '@/lib/utils/deletion-queue';

import { ActivityTrail } from './activity-trail';
import { messageIsCopied, messageIsNotCopied } from './aui-selectors';
import { DeferredCarousels } from './deferred-carousels';
import { shouldRenderMessageError } from './should-render-message-error';
import { TtsSpeechButton, useIsMessageSpeaking } from './tts-speech-button';

interface ToolFallbackProps {
  toolName: string;
  args: unknown;
  result?: unknown;
  status: { type: 'running' | 'complete' | 'incomplete' | 'requires-action' };
  toolCallId: string;
  argsText: string;
}

function useMessageId(): string {
  return useAuiState(({ message }) => message.id);
}

// Must NOT use message.id: assistant-ui swaps the optimistic id for the real id
// mid-stream, which would remount the subtree (and restart the slide-in) on every
// new message. index+branchNumber stay stable across that swap but still differ
// across thread/branch switches.
function useStableMessageKey(): string {
  return useAuiState(({ message, threadListItem }) => {
    const threadId = threadListItem?.id ?? 'thread';
    return `${threadId}:${message.index}:${message.branchNumber}`;
  });
}

function formatMessageTimestamp(date: Date, locale: string): string {
  const options: Intl.DateTimeFormatOptions = isTodayFn(date)
    ? { hour: '2-digit', minute: '2-digit' }
    : { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };

  return new Intl.DateTimeFormat(locale, options).format(date);
}

interface MessageTimestampProps {
  className?: string;
  hideWhenRunning?: boolean;
}

function MessageTimestamp({ className, hideWhenRunning }: MessageTimestampProps) {
  const messageId = useMessageId();
  const messageCreatedAt = useAuiState(({ message }) => message.createdAt);
  const isRunning = useAuiState(({ message }) => message.status?.type === 'running');
  const locale = useLocale();
  const isReadOnly = useReadOnly();
  const storedCreatedAt = useAssistantCloudStore(state =>
    isReadOnly ? undefined : state.getMessage(messageId)?.created_at,
  );

  const createdAt = storedCreatedAt ?? messageCreatedAt;
  if (!createdAt || (hideWhenRunning && isRunning)) return null;

  return (
    <span className={cn('aui-message-timestamp text-xs text-muted-foreground', className)}>
      {formatMessageTimestamp(createdAt, locale)}
    </span>
  );
}

function MessageExtras() {
  const components = useMessageDataComponents();
  const raw = useAuiState(({ message }) => message.metadata?.custom?.dataParts);
  // Filter outside the selector: a fresh array per call loops re-renders (#185).
  const dataParts = Array.isArray(raw) ? raw.filter(isMessageDataPart) : null;

  if (!dataParts?.length) return null;
  const rendered = dataParts.map(({ name, data }, i) => {
    const Component = components[name];
    return Component ? <Component key={`${name}:${i}`} data={data as never} /> : null;
  });
  if (rendered.every(node => node === null)) return null;

  return (
    <div className="aui-message-extras mx-auto w-full max-w-(--thread-max-width) px-2">
      {rendered}
    </div>
  );
}

const DEFAULT_AVATAR_SIZE = 32;
const DEFAULT_GAP = 0.75;
const DEFAULT_USER_AVATAR: TenantAvatarConfig = {
  background: 'var(--primary)',
  foreground: 'var(--primary-foreground)',
  icon: 'User',
};

const DEFAULT_ASSISTANT_AVATAR: TenantAvatarConfig = {
  background: 'var(--muted)',
  foreground: 'var(--muted-foreground)',
  icon: 'Bot',
};

function useMessageAvatars(): TenantMessageAvatarsConfig | null {
  const config = useConfig();
  return config.branding?.components?.messageAvatars ?? null;
}

interface MessageAvatarProps {
  role: 'user' | 'assistant';
  sticky?: boolean;
}

function MessageAvatar({ role, sticky }: MessageAvatarProps) {
  const avatarsConfig = useMessageAvatars();
  const { assetBasePath } = useConfig();

  if (!avatarsConfig?.enabled) return null;

  const defaultConfig = role === 'user' ? DEFAULT_USER_AVATAR : DEFAULT_ASSISTANT_AVATAR;
  const avatarConfig = role === 'user' ? avatarsConfig.user : avatarsConfig.assistant;

  const background = avatarConfig?.background ?? defaultConfig.background;
  const foreground = avatarConfig?.foreground ?? defaultConfig.foreground;
  const icon = avatarConfig?.icon ?? defaultConfig.icon;
  const image = avatarConfig?.image;
  const size = avatarConfig?.size ?? DEFAULT_AVATAR_SIZE;
  const borderRadius = avatarConfig?.borderRadius ?? size / 2;

  const imageSrc = image
    ? image.startsWith('http') || image.startsWith('/')
      ? image
      : assetBasePath
        ? `${assetBasePath}/${image}`
        : null
    : null;

  const iconSize = size * 0.5;

  return (
    <Avatar
      className={cn('aui-message-avatar shrink-0', sticky && 'sticky top-4')}
      style={{
        width: size,
        height: size,
        borderRadius,
        backgroundColor: background,
      }}
    >
      {imageSrc && <AvatarImage src={imageSrc} alt={`${role} avatar`} style={{ borderRadius }} />}
      <AvatarFallback
        style={{
          backgroundColor: background,
          borderRadius,
          color: foreground,
        }}
      >
        {icon && <DynamicIcon name={icon} style={{ width: iconSize, height: iconSize }} />}
      </AvatarFallback>
    </Avatar>
  );
}

function MessageError() {
  const t = useTranslations('messageError');

  const shouldRender = useAuiState(({ message }) => shouldRenderMessageError(message));

  if (!shouldRender) return null;

  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive dark:bg-destructive/5 dark:text-red-200">
        <span className="aui-message-error-message">{t('genericError')}</span>
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
}

function TtsHighlighter({ containerRef }: { containerRef: RefObject<HTMLDivElement | null> }) {
  const wordInfo = useTtsWordInfo();
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !wordInfo) return;
    return highlightWordInDom({ el, word: wordInfo.word, targetOccurrence: wordInfo.occurrence });
  }, [wordInfo, containerRef]);
  return null;
}

function AssistantActionBar() {
  const t = useTranslations();
  const canReload = useCanReloadMessage();
  const api = useAui();
  const threadListItem = useAuiState(({ threadListItem }) => threadListItem);
  const { trackChatEvent, CHAT_ANALYTICS_EVENTS } = useAnalytics();
  const isReadOnly = useReadOnly();
  const ttsConfig = useConfigValue<TtsConfig>('meta.tts');
  const isTtsEnabled = ttsConfig?.enabled ?? false;
  const isTtsActive = useIsMessageSpeaking();

  const handleReload = React.useCallback(async () => {
    const messageState = api.message.getState();
    const threadId = threadListItem?.id;

    if (messageState && threadId) {
      await queueDeletion(threadId, messageState.id);
    }

    stopActiveTts();
    trackChatEvent(CHAT_ANALYTICS_EVENTS.RESPONSE_RETRY_CLICKED, { threadId });
    api.message.reload();
  }, [api, threadListItem, trackChatEvent, CHAT_ANALYTICS_EVENTS]);

  if (isReadOnly) return null;

  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      className={cn(
        'aui-assistant-action-bar-root flex gap-1 text-foreground transition-opacity duration-150',
        canReload || isTtsActive ? 'opacity-100' : 'opacity-0 group-hover/footer:opacity-100',
      )}
    >
      {isTtsEnabled && <TtsSpeechButton />}
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip={t('thread.tooltips.copy')}>
          <AuiIf condition={messageIsCopied}>
            <CheckIcon />
          </AuiIf>
          <AuiIf condition={messageIsNotCopied}>
            <CopyIcon />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      {canReload && (
        <TooltipIconButton tooltip={t('thread.tooltips.refresh')} onClick={handleReload}>
          <RefreshCwIcon />
        </TooltipIconButton>
      )}
    </ActionBarPrimitive.Root>
  );
}

const groupBy = groupPartByType({
  'tool-call': ['group-tool'],
  reasoning: ['group-reasoning'],
});

const ToolFallback = memo(function ToolFallback({
  toolName,
  args,
  result,
  status,
}: ToolFallbackProps) {
  const messageStatus = useAuiState(({ message }) => message.status?.type);
  const { isHandledTool } = useToolGroup();

  if (messageStatus === 'incomplete') return null;
  if (isHandledTool(toolName)) return null;

  // Unhandled tools render no carousel — the activity trail surfaces their
  // progress. ToolDebugInfo stays so debug mode shows raw args/result.
  return (
    <ToolDebugInfo toolName={toolName} args={args} result={result} status={status}>
      {null}
    </ToolDebugInfo>
  );
});

const AssistantMessageInner = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function AssistantMessageInner(incomingProps, ref) {
  const { isDebugMode } = useDebugMode();
  const mountedDuringStreaming = useMountedDuringStreaming();
  const { ToolGroupComponent } = useToolGroup();
  const { messageLocale, messageDir } = useMessageLocale();

  // assistant-ui reuses the same React instance across thread switches (position-
  // based reconciliation); keying forces a remount so stale carousel/Swiper state
  // doesn't leak across threads.
  const stableKey = useStableMessageKey();

  const renderPart = useCallback<
    React.ComponentProps<typeof MessagePrimitive.GroupedParts>['children']
  >(
    ({ part, children }) => {
      switch (part.type) {
        case 'group-tool':
          return (
            <div data-tts-ignore>
              <ToolGroupComponent
                startIndex={part.indices[0]}
                endIndex={part.indices[part.indices.length - 1]}
              >
                {children}
              </ToolGroupComponent>
            </div>
          );
        case 'group-reasoning':
          return isDebugMode ? (
            <div data-tts-ignore>
              <ReasoningGroup
                startIndex={part.indices[0]}
                endIndex={part.indices[part.indices.length - 1]}
              >
                {children}
              </ReasoningGroup>
            </div>
          ) : null;
        case 'text':
          return <MarkdownText />;
        case 'reasoning':
          return isDebugMode ? (
            <div data-tts-ignore>
              <Reasoning {...part} />
            </div>
          ) : null;
        case 'tool-call': {
          const approval = (part as { approval?: ApprovalPartState }).approval;
          return (
            <div data-tts-ignore>
              {part.toolUI ??
                (approval ? (
                  <GenericApprovalCard
                    toolName={part.toolName}
                    toolCallId={part.toolCallId}
                    args={part.args}
                    approval={approval}
                    status={part.status}
                    result={part.result}
                  />
                ) : (
                  <ToolFallback
                    toolName={part.toolName}
                    args={part.args}
                    result={part.result}
                    status={part.status}
                    toolCallId={part.toolCallId}
                    argsText={part.argsText}
                  />
                ))}
            </div>
          );
        }
        case 'image':
          return <MessagePartPrimitive.Image />;
        case 'data':
          return part.dataRendererUI ? <div data-tts-ignore>{part.dataRendererUI}</div> : null;
        default:
          return null;
      }
    },
    [ToolGroupComponent, isDebugMode],
  );

  const avatarsConfig = useMessageAvatars();
  const hasAvatar = avatarsConfig?.enabled;
  const gap = avatarsConfig?.gap ?? DEFAULT_GAP;
  const sticky = avatarsConfig?.sticky ?? false;
  const avatarSize = avatarsConfig?.assistant?.size ?? DEFAULT_AVATAR_SIZE;

  const contentRef = useRef<HTMLDivElement>(null);
  const isThisMessageSpeaking = useIsMessageSpeaking();

  // While a HITL action awaits the user (human-tool card or approval gate), the run is paused
  // (not "running"), so the finished-state footer (timestamp + reload/copy/speak) would otherwise
  // show and read as "done". Hide it until the user acts. Same signal that keeps the trail live.
  const awaitingHitlAction = useAwaitingHitlAction();

  return (
    <div
      ref={ref}
      key={stableKey}
      {...incomingProps}
      className={cn(
        'aui-assistant-message-root relative mx-auto w-full max-w-(--thread-max-width) py-4',
        mountedDuringStreaming && 'animate-in duration-200 fade-in slide-in-from-bottom-1',
        incomingProps.className,
      )}
      data-role="assistant"
    >
      <div
        className={cn('flex items-start', hasAvatar && 'px-2')}
        style={hasAvatar ? { gap: `${gap}rem` } : undefined}
      >
        {hasAvatar && <MessageAvatar role="assistant" sticky={sticky} />}
        <div
          className={cn(
            'aui-assistant-message-content flex min-w-0 flex-1 flex-col leading-7 wrap-break-word text-foreground',
            !hasAvatar && 'px-2',
          )}
        >
          <MessageLocaleWrapper locale={messageLocale}>
            <ActivityTrail />
            <div ref={contentRef} className="relative isolate">
              <MessagePrimitive.GroupedParts groupBy={groupBy}>
                {renderPart}
              </MessagePrimitive.GroupedParts>
              {isThisMessageSpeaking && <TtsHighlighter containerRef={contentRef} />}
            </div>
            <DeferredCarousels />
            <MessageError />
          </MessageLocaleWrapper>
        </div>
      </div>

      {!awaitingHitlAction && (
        <div
          dir={messageDir}
          className={cn(
            'aui-assistant-message-footer group/footer mt-2 flex items-center gap-3',
            !hasAvatar && 'px-2',
          )}
          style={
            hasAvatar
              ? { marginInlineStart: `calc(${avatarSize}px + ${gap}rem + 0.5rem)` }
              : undefined
          }
        >
          <MessageLocaleWrapper locale={messageLocale} inline>
            <MessageTimestamp hideWhenRunning />
          </MessageLocaleWrapper>
          <AssistantActionBar />
        </div>
      )}

      <MessageExtras />
    </div>
  );
});

export function AssistantMessage() {
  return (
    <MessagePrimitive.Root asChild>
      <AssistantMessageInner />
    </MessagePrimitive.Root>
  );
}

export function UserMessage() {
  const mountedDuringStreaming = useMountedDuringStreaming();
  const avatarsConfig = useMessageAvatars();
  const hasAvatar = avatarsConfig?.enabled;
  const gap = avatarsConfig?.gap ?? DEFAULT_GAP;
  const sticky = avatarsConfig?.sticky ?? false;
  const avatarSize = avatarsConfig?.user?.size ?? DEFAULT_AVATAR_SIZE;
  const { messageLocale, messageDir } = useMessageLocale();
  const stableKey = useStableMessageKey();

  return (
    <MessagePrimitive.Root asChild>
      <div
        key={stableKey}
        dir={messageDir}
        className={cn(
          'aui-user-message-root mx-auto w-full max-w-[var(--thread-max-width)] px-2 py-4 first:mt-3 last:mb-5',
          mountedDuringStreaming && 'animate-in duration-200 fade-in slide-in-from-bottom-1',
        )}
        data-role="user"
      >
        <div
          className="flex flex-row-reverse items-start"
          style={hasAvatar ? { gap: `${gap}rem` } : undefined}
        >
          {hasAvatar && <MessageAvatar role="user" sticky={sticky} />}
          <div className="aui-user-message-content-wrapper relative flex min-w-0 flex-1 flex-col items-end">
            <div className="aui-user-message-content w-fit max-w-full rounded-3xl bg-secondary px-5 py-2.5 break-words text-foreground">
              <MessageLocaleWrapper locale={messageLocale}>
                <MessagePrimitive.Parts />
              </MessageLocaleWrapper>
            </div>
          </div>
        </div>

        <div
          className="aui-user-message-footer mt-1 flex justify-end pe-2"
          style={hasAvatar ? { marginInlineEnd: `calc(${avatarSize}px + ${gap}rem)` } : undefined}
        >
          <MessageLocaleWrapper locale={messageLocale} inline>
            <MessageTimestamp />
          </MessageLocaleWrapper>
        </div>

        <MessageExtras />
      </div>
    </MessagePrimitive.Root>
  );
}

export function EditComposer() {
  const t = useTranslations();
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const { preventSubmitIfSensitive } = useSensitiveDataDetection();
  const { showCharLimitError, handleInput, handlePaste, truncateToLimit } = useCharacterLimit();

  const handleSubmit = (e: React.SyntheticEvent) => {
    if (
      preventSubmitIfSensitive({ text: truncateToLimit(inputRef.current?.value || ''), event: e })
    ) {
      return;
    }
  };

  return (
    <div className="aui-edit-composer-wrapper mx-auto flex w-full max-w-[var(--thread-max-width)] flex-col gap-4 px-2 first:mt-4">
      <ComposerPrimitive.Root
        onSubmit={handleSubmit}
        className={cn(
          'aui-edit-composer-root ms-auto flex w-full max-w-7/8 flex-col rounded-xl border bg-muted',
          showCharLimitError ? 'border-destructive' : undefined,
        )}
      >
        <Tooltip open={showCharLimitError} onOpenChange={() => {}}>
          <TooltipTrigger asChild>
            <ComposerPrimitive.Input
              ref={inputRef}
              onInput={handleInput}
              onPaste={handlePaste}
              className="aui-edit-composer-input flex min-h-[60px] w-full resize-none bg-transparent p-4 text-foreground outline-none"
              autoFocus
            />
          </TooltipTrigger>
          <TooltipContent side="top">{t('characterLimit.exceeded')}</TooltipContent>
        </Tooltip>

        <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center justify-center gap-2 self-end">
          <ComposerPrimitive.Cancel asChild>
            <Button variant="ghost" size="sm" aria-label={t('thread.ariaLabels.cancelEdit')}>
              {t('thread.buttons.cancel')}
            </Button>
          </ComposerPrimitive.Cancel>
          <ComposerPrimitive.Send asChild>
            <Button size="sm" aria-label={t('thread.ariaLabels.updateMessage')}>
              {t('thread.buttons.update')}
            </Button>
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
    </div>
  );
}

export function BranchPicker({ className, ...rest }: BranchPickerPrimitive.Root.Props) {
  const t = useTranslations();

  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        'aui-branch-picker-root -ms-2 me-2 inline-flex items-center text-xs text-muted-foreground',
        className,
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip={t('thread.tooltips.previous')}>
          <ChevronLeftIcon className="ltr:block rtl:hidden" />
          <ChevronRightIcon className="ltr:hidden rtl:block" />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip={t('thread.tooltips.next')}>
          <ChevronRightIcon className="ltr:block rtl:hidden" />
          <ChevronLeftIcon className="ltr:hidden rtl:block" />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
}

export function UserActionBar() {
  const t = useTranslations();
  const isReadOnly = useReadOnly();

  if (isReadOnly) return null;

  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-user-action-bar-root flex flex-col items-end"
    >
      <ActionBarPrimitive.Edit asChild>
        <TooltipIconButton tooltip={t('additionalUi.edit')} className="aui-user-action-edit p-4">
          <PencilIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Edit>
    </ActionBarPrimitive.Root>
  );
}
